import React, { useState, useEffect } from 'react';
import { useConfiguration } from '../hooks/useConfiguration';
import { Configuration } from '../types/api';

const ConfigurationPanel: React.FC = () => {
  const { config, loading, error, updating, updateConfig, resetConfig } = useConfiguration();
  const [formData, setFormData] = useState<Partial<Configuration>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (config) {
      setFormData(config);
    }
  }, [config]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateConfig(formData);
      setMessage({ type: 'success', text: 'Configuration updated successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update configuration' });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset the configuration to defaults? This action cannot be undone.')) {
      try {
        await resetConfig();
        setMessage({ type: 'success', text: 'Configuration reset to defaults successfully!' });
        setTimeout(() => setMessage(null), 3000);
      } catch (error) {
        setMessage({ type: 'error', text: 'Failed to reset configuration' });
        setTimeout(() => setMessage(null), 5000);
      }
    }
  };

  const handleChange = (field: keyof Configuration, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (error) {
    return (
      <div>
        {/* <h2 className="panel-title">Configuration Management</h2> */}
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        {/* <h2 className="panel-title">Configuration Management</h2> */}
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <span className="loading-spinner" />
          Loading configuration...
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* <h2 className="panel-title">Configuration Management</h2> */}
      
      {message && (
        <div className={message.type === 'success' ? 'success-message' : 'error-message'}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ maxWidth: '100%', alignContent: 'center' }}>
        <div style={{ display: 'flex', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '26px' }}>
          
          {/* Processing Mode Section */}
          <div style={{ padding: '20px', border: '1px solid #e9ecef', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#495057' }}>Processing Mode</h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                Processing Mode:
              </label>
              <select
                value={formData.processingMode || 'daily'}
                onChange={(e) => handleChange('processingMode', e.target.value as 'daily' | 'hourly')}
                style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
              >
                <option value="daily">Daily</option>
                <option value="hourly">Hourly</option>
              </select>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                Task Type:
              </label>
              <select
                value={formData.taskType || 'both'}
                onChange={(e) => handleChange('taskType', e.target.value as 'created' | 'updated' | 'both')}
                style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
              >
                <option value="created">Created Only</option>
                <option value="updated">Updated Only</option>
                <option value="both">Both Created & Updated</option>
              </select>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={formData.schedulerEnabled || false}
                  onChange={(e) => handleChange('schedulerEnabled', e.target.checked)}
                />
                <span style={{ fontWeight: '500' }}>Enable Scheduler</span>
              </label>
            </div>
          </div>

          {/* Time Configuration */}
          <div style={{ padding: '20px', border: '1px solid #e9ecef', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#495057' }}>Time Configuration</h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                Created Days Lookback:
              </label>
              <input
                type="number"
                min="0"
                value={formData.createdXXDayBefore || 1}
                onChange={(e) => handleChange('createdXXDayBefore', parseInt(e.target.value))}
                style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                Updated Days Lookback:
              </label>
              <input
                type="number"
                min="0"
                value={formData.updatedXXDayBefore || 1}
                onChange={(e) => handleChange('updatedXXDayBefore', parseInt(e.target.value))}
                style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                Timezone:
              </label>
              <input
                type="text"
                value={formData.timezone || 'UTC'}
                onChange={(e) => handleChange('timezone', e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
              />
            </div>
          </div>

          {/* Hourly Mode Settings */}
          {formData.processingMode === 'hourly' && (
            <div style={{ padding: '20px', border: '1px solid #e9ecef', borderRadius: '8px', backgroundColor: '#fff3cd' }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#495057' }}>Hourly Mode Settings</h3>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Hours Lookback:
                </label>
                <input
                  type="number"
                  min="1"
                  max="24"
                  value={formData.hoursLookback || 2}
                  onChange={(e) => handleChange('hoursLookback', parseInt(e.target.value))}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Hourly Window Size (hours):
                </label>
                <input
                  type="number"
                  min="1"
                  max="6"
                  value={formData.hourlyWindowSize || 1}
                  onChange={(e) => handleChange('hourlyWindowSize', parseInt(e.target.value))}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
                />
              </div>
            </div>
          )}

          {/* Cron Schedule */}
          <div style={{ padding: '20px', border: '1px solid #e9ecef', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#495057' }}>Schedule Configuration</h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                Cron Schedule:
              </label>
              <input
                type="text"
                value={formData.cronSchedule || '0 9 * * *'}
                onChange={(e) => handleChange('cronSchedule', e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
                placeholder="0 9 * * *"
              />
            </div>

            {formData.cronDescription && (
              <div style={{ fontSize: '0.9rem', color: '#6c757d', fontStyle: 'italic' }}>
                {formData.cronDescription}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ marginTop: '30px', display: 'grid', gap: '15px', alignItems: 'center' }}>
          <button
            type="submit"
            disabled={updating}
            style={{
              padding: '12px 24px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: updating ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
              opacity: updating ? 0.6 : 1,
            }}
          >
            {updating ? (
              <>
                <span className="loading-spinner" />
                Updating...
              </>
            ) : (
              'Update Configuration'
            )}
          </button>

          <button
            type="button"
            onClick={handleReset}
            disabled={updating}
            style={{
              padding: '12px 24px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: updating ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
              opacity: updating ? 0.6 : 1,
            }}
          >
            Reset to Defaults
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConfigurationPanel;